'use client'

import { useParams, getTranslations } from '@/lib/next-shim'
import Container from '@/components/ui/layouts/Container'
import { BuildingOfficeIcon } from '@heroicons/react/20/solid'
import { useGetEstablishmentQuery } from '@/core/graphql/hooks'
import { MeetingLinkCard } from './MeetingLinkCard'

export function EstablishmentPage() {
  const params = useParams()
  const establishmentSlug = params?.establishment as string
  const t = getTranslations('page.Establishment')

  const { data, loading, error } = useGetEstablishmentQuery({
    variables: { slug: establishmentSlug },
    skip: !establishmentSlug,
  })

  if (loading) return <div className="p-10 text-center">{t('common.loading')}</div>
  // Never surface raw backend/DB error messages to patients (e.g. "No model found with
  // filter {slug:...}"). Log the technical detail for developers, show a friendly message.
  if (error) {
    console.error('[EstablishmentPage] failed to load establishment:', error)
    return <div className="p-10 text-center">{t('common.establishmentNotFound')}</div>
  }

  const establishment = data?.establishment
  if (!establishment) return <div className="p-10 text-center">{t('common.establishmentNotFound')}</div>

  const meetingLinks = establishment.meetingLinks || []

  return (
    <main className="flex-1 min-h-[600px]">
      <header className="relative">
        <Container className="bg-primary py-4">
          <div className="flex items-center">
            <div className="flex items-center gap-x-6">
              <div className="h-16 w-16 bg-white rounded-full p-4">
                <BuildingOfficeIcon />
              </div>
              <h1>
                <div className="mt-1 text-base font-semibold leading-6 text-white">{establishment.name}</div>
                <div className="text-sm leading-6 text-gray-400">{t('header.subtitle')}</div>
              </h1>
            </div>
          </div>
        </Container>
      </header>

      <Container className="py-4">
        <div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-y-8 md:mx-0 md:max-w-none md:grid-cols-3">
          <div className="hidden sm:block sticky top-[68px] md:col-start-3 md:row-end-1">
            <div className="rounded-lg bg-white shadow-sm ring-1 ring-gray-900/5">
              <div className="px-4 py-5 sm:px-6">
                <h2 className="text-lg font-medium leading-6 text-gray-900">{t('summarySection.title')}</h2>
              </div>
              <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-3 flex justify-between sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">{t('summarySection.meetingLinks')}</dt>
                    <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{meetingLinks.length}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:col-span-2 md:row-span-2 md:row-end-2">
            {meetingLinks.map((meetingLink) => (
              <MeetingLinkCard
                key={meetingLink._id}
                link={`/meeting-links/${meetingLink.slug}`}
                name={meetingLink.name}
                specialities={meetingLink.specialities}
              />
            ))}
          </div>
        </div>
      </Container>
    </main>
  )
}
